import { useCallback, useMemo, useState } from 'react';
import { MultiValue, SingleValue } from 'react-select';
import { DateTime } from 'luxon';

import RFPResponseWithRFP from 'types/api/RFPResponseWithRFP';
import { InputOption } from 'components/forms/raw/SelectInput';

type Sort = (a: RFPResponseWithRFP, b: RFPResponseWithRFP) => number;
type InputOptionWithSort = InputOption & { sort: Sort };

const sortOptions: InputOptionWithSort[] = [
  {
    value: 'projectName',
    label: 'Project Name',
    sort: (a, b) => (a.project_name ?? '').localeCompare(b.project_name ?? ''),
  },
  {
    value: 'closeDate',
    label: 'Close Date',
    sort: (a, b) => {
      const aDate = DateTime.fromISO(a.traro_rfp.close_date);
      const bDate = DateTime.fromISO(b.traro_rfp.close_date);
      const now = DateTime.now();

      return now.toMillis() - aDate.toMillis() - (now.toMillis() - bDate.toMillis());
    },
  },
  { value: 'status', label: 'Status', sort: (a, b) => a.rfp_response_status.localeCompare(b.rfp_response_status) },
];

const useSortedData = (data: RFPResponseWithRFP[]) => {
  const [sort, setSort] = useState<SingleValue<InputOptionWithSort>>(sortOptions[0]);

  const onChange = useCallback(
    (value: SingleValue<InputOptionWithSort> | MultiValue<InputOptionWithSort>) => {
      setSort(value as SingleValue<InputOptionWithSort>);
    },
    [setSort],
  );

  const sortedData = useMemo(() => [...data].sort(sort?.sort), [sort, data]);

  return {
    sortValue: sort,
    onSortChange: onChange,
    sortOptions,
    sortedData,
  } as const;
};

export default useSortedData;
